/**
 * PATCH /collector/users/:user_id — request body contract + form → API mapper.
 */
import { Language } from './types/UserProfile';
import { isValidEmail, isValidPhoneNumber } from '../../Utils/validation';

/** Exact keys sent in JSON body (snake_case). */
export interface UpdateCollectorUserBasicInfo {
  first_name: string;
  middle_name: string;
  last_name: string;
  preferred_name: string;
  date_of_birth: string;
  gender: string;
  gender_identity: string;
  pronouns: string;
  languages: number[];
}

export interface UpdateCollectorUserAddress {
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip_code: string;
}

export interface UpdateCollectorUserPayload {
  basic_info: UpdateCollectorUserBasicInfo;
  address: UpdateCollectorUserAddress;
  phone_number: string;
  // Omitted for dependents: they inherit the guardian's client tag(s) and the
  // API rejects direct dependent tag writes, so the key must not be sent.
  client_ids?: number[];
  /** Rides with client_ids, but can be sent alone — omitting client_ids grandfathers the user's existing tag set. */
  client_division_ids?: number[];
  guardian_relationship?: string;
  email?: string;
  /** Max 40 characters, enforced by the backend. */
  external_id?: string;
}

/** Collector edit-user form state (camelCase) — maps to API payload. */
export interface CollectorUserEditFormState {
  firstName: string;
  middleName: string;
  lastName: string;
  preferredName: string;
  dateOfBirth: string;
  gender: string;
  genderIdentity: string;
  pronouns: string;
  selectedLanguages: Language[];
  address1: string;
  address2: string;
  city: string;
  state: string;
  zipCode: string;
  phoneNumber: string;
  email?: string;
  /** All selected client tag ids (matches create-patient multi-select). */
  selectedClientTagIds: number[];
  /** Selected client division id (single — patients get exactly one division). */
  selectedClientDivisionId?: number | null;
  /** Dependent-only. */
  guardianRelationship?: string;
  /** Staff-only "User IDs" section field. */
  externalId?: string;
}

function toIntIds(values: unknown[]): number[] {
  return values.map((v) => Number(v)).filter((n) => Number.isInteger(n) && !Number.isNaN(n));
}

/**
 * Maps camelCase form state → snake_case payload expected by PATCH /collector/users/:id.
 */
export function mapUserEditFormToApiPayload(
  form: CollectorUserEditFormState,
  options: { isDependent?: boolean } = {},
): UpdateCollectorUserPayload {
  const languageIds = toIntIds(form.selectedLanguages.map((l) => l.id));
  const payload: UpdateCollectorUserPayload = {
    basic_info: {
      first_name: form.firstName ?? '',
      middle_name: form.middleName ?? '',
      last_name: form.lastName ?? '',
      preferred_name: form.preferredName ?? '',
      date_of_birth: form.dateOfBirth ?? '',
      gender: form.gender ?? '',
      gender_identity: form.genderIdentity ?? '',
      pronouns: form.pronouns ?? '',
      languages: languageIds,
    },
    address: {
      address1: form.address1 ?? '',
      address2: form.address2 ?? '',
      city: form.city ?? '',
      state: form.state ?? '',
      zip_code: form.zipCode ?? '',
    },
    phone_number: form.phoneNumber ?? '',
    guardian_relationship: form.guardianRelationship?.trim() || undefined,
    email: form.email?.trim() || undefined,
    external_id: form.externalId?.trim() || undefined,
  };
  // Dependents inherit the guardian's client tag(s). The dependent edit form
  // has no client-tag field and the API rejects direct dependent tag writes,
  // so omit client_ids entirely for them.
  if (!options.isDependent) {
    payload.client_ids = toIntIds(form.selectedClientTagIds ?? []).slice(0, 1);
    payload.client_division_ids = form.selectedClientDivisionId ? [Number(form.selectedClientDivisionId)] : [];
  }
  return payload;
}

/** Alias for role-based adapters (identical to {@link mapUserEditFormToApiPayload}). */
export const mapCollectorUserEditFormToApiPayload = mapUserEditFormToApiPayload;

/**
 * Returns a deep-cloned plain object safe for JSON.stringify (no undefined).
 * Throws if structure cannot be serialized.
 */
export function buildCollectorUserPatchJsonBody(payload: UpdateCollectorUserPayload): UpdateCollectorUserPayload {
  const languageIds = payload.basic_info.languages
    .map((n) => Number(n))
    .filter((n) => Number.isInteger(n) && !Number.isNaN(n));

  const body: UpdateCollectorUserPayload = {
    basic_info: {
      first_name: String(payload.basic_info.first_name),
      middle_name: String(payload.basic_info.middle_name),
      last_name: String(payload.basic_info.last_name),
      preferred_name: String(payload.basic_info.preferred_name),
      date_of_birth: String(payload.basic_info.date_of_birth),
      gender: String(payload.basic_info.gender),
      gender_identity: String(payload.basic_info.gender_identity),
      pronouns: String(payload.basic_info.pronouns),
      languages: languageIds,
    },
    address: {
      address1: String(payload.address.address1),
      address2: String(payload.address.address2),
      city: String(payload.address.city),
      state: String(payload.address.state),
      zip_code: String(payload.address.zip_code),
    },
    phone_number: String(payload.phone_number ?? ''),
    guardian_relationship: payload.guardian_relationship ? String(payload.guardian_relationship) : undefined,
    external_id: payload.external_id ? String(payload.external_id) : undefined,
  };

  // Only forward client_ids when present. Dependents omit it (inherited from
  // guardian); sending it would be rejected by the API.
  if (Array.isArray(payload.client_ids)) {
    body.client_ids = payload.client_ids.map((n) => Number(n)).filter((n) => Number.isInteger(n) && !Number.isNaN(n));
  }
  if (Array.isArray(payload.client_division_ids)) {
    body.client_division_ids = payload.client_division_ids
      .map((n) => Number(n))
      .filter((n) => Number.isInteger(n) && !Number.isNaN(n));
  }

  try {
    JSON.stringify(body);
  } catch {
    throw new Error('INVALID_COLLECTOR_USER_JSON');
  }

  return body;
}

/** Client-side validation before PATCH. Null = OK; string = user-facing error.
 *
 * `isDependent` exempts dependents from the phone-number check — dependents
 * don't own a phone, the form doesn't render the field as required (no
 * asterisk), but until this check was scoped, saving a dependent profile
 * via the collector edit flow surfaced "Phone number is required." even
 * though the field is intentionally blank for that role.
 */
export function validateUpdateCollectorUserPayload(
  payload: UpdateCollectorUserPayload,
  options: { isDependent?: boolean } = {},
): string | null {
  if (!payload || typeof payload !== 'object') {
    return 'Invalid request payload. Please try again.';
  }
  if (!payload.basic_info || !payload.address) {
    return 'Invalid request payload. Please try again.';
  }

  try {
    JSON.stringify(payload);
  } catch {
    return 'Invalid request payload. Please try again.';
  }

  if (!Array.isArray(payload.basic_info.languages)) {
    return 'Invalid request payload. Please try again.';
  }
  if (payload.basic_info.languages.some((n) => typeof n !== 'number' || !Number.isInteger(n))) {
    return 'Invalid request payload. Please try again.';
  }
  // Dependents omit client_ids (inherited from guardian); only validate when
  // the caller actually sends tag ids.
  if (payload.client_ids !== undefined) {
    if (!Array.isArray(payload.client_ids)) {
      return 'Invalid request payload. Please try again.';
    }
    if (payload.client_ids.some((n) => typeof n !== 'number' || !Number.isInteger(n))) {
      return 'Invalid request payload. Please try again.';
    }
  }

  if (!payload.basic_info.first_name.trim() || !payload.basic_info.last_name.trim()) {
    return 'First and last name are required.';
  }
  if (!payload.basic_info.date_of_birth.trim()) {
    return 'Date of birth is required.';
  }
  if (!payload.basic_info.gender.trim() || !payload.basic_info.gender_identity.trim()) {
    return 'Gender fields are required.';
  }
  if (payload.basic_info.languages.length === 0) {
    return 'Select at least one language.';
  }
  if (!payload.address.address1.trim() || !payload.address.city.trim() || !payload.address.zip_code.trim()) {
    return 'Address, city, and zip code are required.';
  }
  if (!payload.address.state.trim()) {
    return 'State is required.';
  }
  if (!options.isDependent && !payload.phone_number.trim()) {
    return 'Phone number is required.';
  }
  if (payload.phone_number && !isValidPhoneNumber(payload.phone_number)) {
    return 'Please enter a valid phone number.';
  }
  if (payload.email && !isValidEmail(payload.email)) {
    return 'Please enter a valid email address.';
  }
  if (payload.external_id && payload.external_id.length > 40) {
    return 'External User ID must be 40 characters or fewer.';
  }

  return null;
}
